import { ReactNode, Ref } from 'react';
import { FieldError, Group, Input, Label, Text } from 'react-aria-components';

import { styles } from './UiKitInputSearch.styles';

type InputProps = React.ComponentPropsWithoutRef<typeof Input>;
type BaseInputProps = Omit<InputProps, 'isInvalid'>;

export interface UiKitInputSearchProps extends BaseInputProps {
  label?: string;
  helperText?: string;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  errorMessage?: string;
  ref?: Ref<HTMLDivElement>;
  className?: string;
  withBorder?: boolean;
  error?: boolean;
  onSearch?: (query: string) => void;
  required?: boolean;
}

export function UiKitInputSearch({
  startAdornment,
  endAdornment,
  label: labelProp,
  helperText,
  errorMessage,
  ref,
  className,
  withBorder = true,
  error,
  onSearch,
  required,
  ...props
}: UiKitInputSearchProps) {
  const slots = styles({
    error: !!errorMessage || error,
    withStartAdornment: !!startAdornment,
    withBorder
  });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onSearch) {
      onSearch(e.currentTarget.value);
    }
    props.onKeyDown?.(e);
  };

  const label = required ? `${labelProp} *` : labelProp;
  const placeholder =
    required && props.placeholder
      ? `${props.placeholder} *`
      : props.placeholder;

  return (
    <>
      {label && <Label className={slots.label()}>{label}</Label>}
      <Group
        className={slots.wrapper()}
        ref={ref}
      >
        {startAdornment && (
          <div className={slots.startAdornment()}>{startAdornment}</div>
        )}
        <Input
          className={slots.input({ class: className })}
          {...props}
          placeholder={placeholder}
          onKeyDown={handleKeyDown}
          required={required}
        />
        {endAdornment && (
          <div className={slots.endAdornment()}>{endAdornment}</div>
        )}
      </Group>
      {helperText && (
        <Text
          slot="description"
          className={slots.helperText()}
        >
          {helperText}
        </Text>
      )}
      <FieldError className={slots.helperText()}>{errorMessage}</FieldError>
    </>
  );
}
